import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CalendarOff, Plus, Trash2 } from "lucide-react";
import { API, auth, inputClass, labelClass, primaryBtn, ghostBtn, handleAuthError } from "./shared";
import { useConfirm } from "./useConfirm";

const fmt = (d) => new Date(`${d}T12:00:00`).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

const AdminClosedDays = ({ token, logout }) => {
  const [items, setItems] = useState([]);
  const [date, setDate] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const { confirm, dialog } = useConfirm();

  const load = () => axios.get(`${API}/closed-days`).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/closed-days`, { date, reason }, auth(token));
      toast.success("Jour bloqué : aucun rendez-vous ne pourra être pris");
      setDate("");
      setReason("");
      load();
    } catch (err) {
      handleAuthError(err, logout, () => toast.error(err.response?.data?.detail || "Échec de l'ajout"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (d) => {
    const ok = await confirm({ title: "Réouvrir ce jour ?", description: `Les clients pourront de nouveau réserver le ${fmt(d.date)}.`, action: "Oui, réouvrir" });
    if (!ok) return;
    try {
      await axios.delete(`${API}/closed-days/${d.id}`, auth(token));
      toast.success("Jour réouvert");
      load();
    } catch (err) {
      handleAuthError(err, logout, () => toast.error("Échec de la suppression"));
    }
  };

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = items.filter((d) => d.date >= today);
  const past = items.filter((d) => d.date < today);

  return (
    <div data-testid="admin-closed-days-tab">
      {dialog}
      <form onSubmit={add} className="rounded-3xl bg-cream p-8" data-testid="admin-closed-day-form">
        <h2 className="font-display flex items-center gap-2 text-lg font-bold text-forest">
          <CalendarOff className="h-5 w-5 text-gold" />
          Bloquer un jour (congé, jour férié, inventaire...)
        </h2>
        <p className="mt-2 text-sm text-brand-hover">Les dimanches sont déjà fermés automatiquement. Aucun créneau ne sera proposé aux clients ce jour-là.</p>
        <div className="mt-6 grid gap-5 sm:grid-cols-2">
          <div>
            <label className={labelClass}>Date *</label>
            <input type="date" required min={today} value={date} onChange={(e) => setDate(e.target.value)} data-testid="admin-closed-day-date-input" className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Motif (affiché aux clients)</label>
            <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ex : Fête de l'Indépendance" data-testid="admin-closed-day-reason-input" className={inputClass} />
          </div>
        </div>
        <button type="submit" disabled={saving} data-testid="admin-closed-day-submit-button" className={`mt-6 ${primaryBtn}`}>
          <Plus className="h-4 w-4" />
          {saving ? "Enregistrement..." : "Bloquer ce jour"}
        </button>
      </form>

      <h2 className="font-display mt-14 text-lg font-bold text-forest">
        Jours de fermeture à venir <span className="text-gold">({upcoming.length})</span>
      </h2>
      <div className="mt-6 grid gap-3 md:grid-cols-2">
        {upcoming.map((d, i) => (
          <div key={d.id} className="flex items-center justify-between gap-4 rounded-2xl border border-brand/10 bg-white px-5 py-4" data-testid={`admin-closed-day-${i}`}>
            <div>
              <p className="font-display text-base font-bold capitalize text-forest">{fmt(d.date)}</p>
              <p className="text-xs text-brand">{d.reason || "Fermeture exceptionnelle"}</p>
            </div>
            <button onClick={() => remove(d)} data-testid={`admin-closed-day-${i}-delete`} className={`${ghostBtn} hover:border-gold hover:text-gold`}>
              <Trash2 className="h-3.5 w-3.5" /> Réouvrir
            </button>
          </div>
        ))}
        {upcoming.length === 0 && <p className="col-span-full rounded-2xl border-2 border-dashed border-brand/20 px-6 py-8 text-sm text-brand">Aucun jour bloqué à venir.</p>}
      </div>
      {past.length > 0 && (
        <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.2em] text-brand/60">{past.length} fermeture(s) passée(s)</p>
      )}
    </div>
  );
};

export default AdminClosedDays;
